/**
 * Helper Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides utility functions for debounce, throttle,
 * object manipulation, and other common operations.
 */

/**
 * Function Control Utilities
 */
export const functionHelpers = {
  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} Debounced function
   */
  debounce: function(func, delay) {
    let timeoutId;

    return function() {
      const context = this;
      const args = arguments;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(function() {
        func.apply(context, args);
      }, delay);
    };
  },

  /**
   * Throttle function execution
   * @param {Function} func - Function to throttle
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} Throttled function
   */
  throttle: function(func, delay) {
    let lastCall = 0;

    return function() {
      const now = Date.now();
      const context = this;
      const args = arguments;

      if (now - lastCall >= delay) {
        lastCall = now;
        func.apply(context, args);
      }
    };
  },

  /**
   * Create a function that can only be called once
   * @param {Function} func - Function to wrap
   * @returns {Function} Once-only function
   */
  once: function(func) {
    let called = false;
    let result;

    return function() {
      if (!called) {
        called = true;
        result = func.apply(this, arguments);
      }
      return result;
    };
  },

  /**
   * Retry function with exponential backoff
   * @param {Function} func - Function to retry
   * @param {number} maxAttempts - Maximum retry attempts
   * @param {number} baseDelay - Base delay in milliseconds
   * @returns {Promise} Promise that resolves when function succeeds or max attempts reached
   */
  retry: function(func, maxAttempts, baseDelay) {
    maxAttempts = maxAttempts || 3;
    baseDelay = baseDelay || 1000;

    return new Promise(function(resolve, reject) {
      let attempt = 0;

      function executeAttempt() {
        attempt++;

        Promise.resolve(func())
          .then(resolve)
          .catch(function(error) {
            if (attempt >= maxAttempts) {
              reject(error);
            } else {
              const delay = baseDelay * Math.pow(2, attempt - 1);
              setTimeout(executeAttempt, delay);
            }
          });
      }

      executeAttempt();
    });
  }
};

/**
 * Object Manipulation Utilities
 */
export const objectHelpers = {
  /**
   * Deep clone an object (ES5 compatible)
   * @param {any} obj - Object to clone
   * @returns {any} Cloned object
   */
  deepClone: function(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }

    if (Array.isArray(obj)) {
      return obj.map(function(item) {
        return objectHelpers.deepClone(item);
      });
    }

    const cloned = {};
    Object.keys(obj).forEach(function(key) {
      cloned[key] = objectHelpers.deepClone(obj[key]);
    });

    return cloned;
  },

  /**
   * Deep merge objects (ES5 compatible)
   * @param {Object} target - Target object
   * @param {Object} source - Source object
   * @returns {Object} Merged object
   */
  deepMerge: function(target, source) {
    if (!target || typeof target !== 'object') target = {};
    if (!source || typeof source !== 'object') return target;

    const result = objectHelpers.deepClone(target);

    Object.keys(source).forEach(function(key) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = objectHelpers.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    });

    return result;
  },

  /**
   * Get nested object property safely
   * @param {Object} obj - Object to access
   * @param {string} path - Property path (e.g., 'user.profile.name')
   * @param {any} defaultValue - Default value if property not found
   * @returns {any} Property value or default
   */
  getNestedProperty: function(obj, path, defaultValue) {
    if (!obj || typeof obj !== 'object' || !path) {
      return defaultValue;
    }

    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length; i++) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return defaultValue;
      }
      current = current[keys[i]];
    }

    return current !== undefined ? current : defaultValue;
  },

  /**
   * Set nested object property safely
   * @param {Object} obj - Object to modify
   * @param {string} path - Property path
   * @param {any} value - Value to set
   * @returns {Object} Modified object
   */
  setNestedProperty: function(obj, path, value) {
    if (!obj || typeof obj !== 'object' || !path) {
      return obj;
    }

    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return obj;
  },

  /**
   * Remove empty properties from object
   * @param {Object} obj - Object to clean
   * @returns {Object} Cleaned object
   */
  removeEmptyProperties: function(obj) {
    if (!obj || typeof obj !== 'object') return obj;

    const cleaned = {};

    Object.keys(obj).forEach(function(key) {
      const value = obj[key];

      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value) && value.length > 0) {
          cleaned[key] = value;
        } else if (typeof value === 'object') {
          const cleanedNested = objectHelpers.removeEmptyProperties(value);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested;
          }
        } else {
          cleaned[key] = value;
        }
      }
    });

    return cleaned;
  },

  /**
   * Check if two objects are equal (shallow comparison)
   * @param {Object} obj1 - First object
   * @param {Object} obj2 - Second object
   * @returns {boolean} True if objects are equal
   */
  shallowEqual: function(obj1, obj2) {
    if (obj1 === obj2) return true;

    if (!obj1 || !obj2 || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (let i = 0; i < keys1.length; i++) {
      const key = keys1[i];
      if (obj1[key] !== obj2[key]) return false;
    }

    return true;
  }
};

/**
 * Array Manipulation Utilities
 */
export const arrayHelpers = {
  /**
   * Remove duplicates from array
   * @param {Array} array - Array to deduplicate
   * @param {Function} keyFunc - Function to extract comparison key
   * @returns {Array} Deduplicated array
   */
  removeDuplicates: function(array, keyFunc) {
    if (!Array.isArray(array)) return [];

    if (!keyFunc) {
      // Simple deduplication
      const seen = {};
      return array.filter(function(item) {
        const key = JSON.stringify(item);
        if (seen[key]) return false;
        seen[key] = true;
        return true;
      });
    }

    // Deduplication with key function
    const seen = {};
    return array.filter(function(item) {
      const key = keyFunc(item);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  },

  /**
   * Sort array by multiple criteria
   * @param {Array} array - Array to sort
   * @param {Array} sortCriteria - Array of sort criteria objects
   * @returns {Array} Sorted array
   */
  multiSort: function(array, sortCriteria) {
    if (!Array.isArray(array) || !Array.isArray(sortCriteria)) {
      return array;
    }

    return array.slice().sort(function(a, b) {
      for (let i = 0; i < sortCriteria.length; i++) {
        const criteria = sortCriteria[i];
        const aValue = objectHelpers.getNestedProperty(a, criteria.field);
        const bValue = objectHelpers.getNestedProperty(b, criteria.field);

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        if (criteria.direction === 'desc') {
          comparison = -comparison;
        }

        if (comparison !== 0) return comparison;
      }

      return 0;
    });
  },

  /**
   * Group array by key
   * @param {Array} array - Array to group
   * @param {string|Function} keyOrFunc - Key string or function to extract grouping key
   * @returns {Object} Grouped object
   */
  groupBy: function(array, keyOrFunc) {
    if (!Array.isArray(array)) return {};

    const getKey = typeof keyOrFunc === 'function'
      ? keyOrFunc
      : function(item) { return objectHelpers.getNestedProperty(item, keyOrFunc); };

    const grouped = {};

    array.forEach(function(item) {
      const key = getKey(item);
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    return grouped;
  },

  /**
   * Chunk array into smaller arrays
   * @param {Array} array - Array to chunk
   * @param {number} size - Chunk size
   * @returns {Array} Array of chunks
   */
  chunk: function(array, size) {
    if (!Array.isArray(array) || size <= 0) return [];

    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }

    return chunks;
  }
};

/**
 * String Utilities
 */
export const stringHelpers = {
  /**
   * Generate random string
   * @param {number} length - String length
   * @param {string} charset - Character set to use
   * @returns {string} Random string
   */
  generateRandomString: function(length, charset) {
    length = length || 10;
    charset = charset || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    return result;
  },

  /**
   * Generate unique ID
   * @param {string} prefix - Optional prefix
   * @returns {string} Unique ID
   */
  generateId: function(prefix) {
    prefix = prefix || 'id';
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);

    return prefix + '-' + timestamp + '-' + random;
  },

  /**
   * Escape HTML characters
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml: function(str) {
    if (!str || typeof str !== 'string') return '';

    const escapeMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '/': '&#x2F;'
    };

    return str.replace(/[&<>"'\/]/g, function(match) {
      return escapeMap[match];
    });
  },

  /**
   * Convert string to slug (URL-friendly)
   * @param {string} str - String to convert
   * @returns {string} Slug string
   */
  toSlug: function(str) {
    if (!str || typeof str !== 'string') return '';

    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }
};

/**
 * Math and Calculation Utilities
 */
export const mathHelpers = {
  /**
   * Round number to specific decimal places
   * @param {number} number - Number to round
   * @param {number} decimals - Number of decimal places
   * @returns {number} Rounded number
   */
  roundToDecimals: function(number, decimals) {
    if (isNaN(number)) return 0;

    decimals = decimals || 0;
    const factor = Math.pow(10, decimals);

    return Math.round(number * factor) / factor;
  },

  /**
   * Calculate percentage
   * @param {number} value - Value
   * @param {number} total - Total value
   * @returns {number} Percentage (0-1)
   */
  calculatePercentage: function(value, total) {
    if (!total || total === 0) return 0;
    return value / total;
  },

  /**
   * Clamp number between min and max
   * @param {number} number - Number to clamp
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @returns {number} Clamped number
   */
  clamp: function(number, min, max) {
    return Math.min(Math.max(number, min), max);
  },

  /**
   * Check if number is in range
   * @param {number} number - Number to check
   * @param {number} min - Minimum value (inclusive)
   * @param {number} max - Maximum value (inclusive)
   * @returns {boolean} True if in range
   */
  isInRange: function(number, min, max) {
    return number >= min && number <= max;
  },

  /**
   * Calculate arbitrage margin
   * @param {Array} odds - Array of odds values
   * @returns {number} Arbitrage margin (positive = profitable)
   */
  calculateArbitrageMargin: function(odds) {
    if (!Array.isArray(odds) || odds.length < 2) return 0;

    const inverseSum = odds.reduce(function(sum, odd) {
      return sum + (1 / odd);
    }, 0);

    return 1 - inverseSum;
  },

  /**
   * Calculate optimal stake distribution for arbitrage
   * @param {Array} odds - Array of odds values
   * @param {number} totalStake - Total stake amount
   * @returns {Array} Array of individual stakes
   */
  calculateOptimalStakes: function(odds, totalStake) {
    if (!Array.isArray(odds) || odds.length < 2 || !totalStake || totalStake <= 0) {
      return [];
    }

    const inverseSum = odds.reduce(function(sum, odd) {
      return sum + (1 / odd);
    }, 0);

    return odds.map(function(odd) {
      const stake = totalStake / (odd * inverseSum);
      return mathHelpers.roundToDecimals(stake, 2);
    });
  }
};

/**
 * Date and Time Utilities
 */
export const dateHelpers = {
  /**
   * Check if date is today
   * @param {Date|string} date - Date to check
   * @returns {boolean} True if date is today
   */
  isToday: function(date) {
    if (!date) return false;

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return false;

    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  },

  /**
   * Check if date is in the future
   * @param {Date|string} date - Date to check
   * @returns {boolean} True if date is in future
   */
  isFuture: function(date) {
    if (!date) return false;

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return false;

    return dateObj.getTime() > Date.now();
  },

  /**
   * Add days to date
   * @param {Date|string} date - Base date
   * @param {number} days - Days to add
   * @returns {Date} New date
   */
  addDays: function(date, days) {
    const dateObj = date instanceof Date ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return new Date();

    dateObj.setDate(dateObj.getDate() + days);
    return dateObj;
  },

  /**
   * Get start of day
   * @param {Date|string} date - Date
   * @returns {Date} Start of day
   */
  startOfDay: function(date) {
    const dateObj = date instanceof Date ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return new Date();

    dateObj.setHours(0, 0, 0, 0);
    return dateObj;
  },

  /**
   * Get end of day
   * @param {Date|string} date - Date
   * @returns {Date} End of day
   */
  endOfDay: function(date) {
    const dateObj = date instanceof Date ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return new Date();

    dateObj.setHours(23, 59, 59, 999);
    return dateObj;
  }
};

/**
 * Browser and Device Utilities
 */
export const browserHelpers = {
  /**
   * Check if running in browser
   * @returns {boolean} True if in browser environment
   */
  isBrowser: function() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  },

  /**
   * Check if device is mobile
   * @returns {boolean} True if mobile device
   */
  isMobile: function() {
    if (!this.isBrowser()) return false;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  /**
   * Check if device is tablet
   * @returns {boolean} True if tablet device
   */
  isTablet: function() {
    if (!this.isBrowser()) return false;

    return /iPad|Android/i.test(navigator.userAgent) && !this.isMobile();
  },

  /**
   * Get viewport dimensions
   * @returns {Object} Viewport width and height
   */
  getViewportSize: function() {
    if (!this.isBrowser()) return { width: 0, height: 0 };

    return {
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight
    };
  },

  /**
   * Copy text to clipboard
   * @param {string} text - Text to copy
   * @returns {Promise} Promise that resolves when copied
   */
  copyToClipboard: function(text) {
    if (!this.isBrowser()) {
      return Promise.reject(new Error('Not in browser environment'));
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }

    // Fallback for older browsers
    return new Promise(function(resolve, reject) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy command failed'));
        }
      } catch (err) {
        document.body.removeChild(textArea);
        reject(err);
      }
    });
  }
};

/**
 * Performance Utilities
 */
export const performanceHelpers = {
  /**
   * Measure function execution time
   * @param {Function} func - Function to measure
   * @param {string} label - Label for measurement
   * @returns {any} Function result
   */
  measureTime: function(func, label) {
    if (!func || typeof func !== 'function') return null;

    label = label || 'Function execution';
    const startTime = performance.now();

    const result = func();

    const endTime = performance.now();
    const duration = endTime - startTime;

    console.log(label + ' took ' + duration.toFixed(2) + ' milliseconds');

    return result;
  },

  /**
   * Create performance marker
   * @param {string} name - Marker name
   */
  mark: function(name) {
    if (browserHelpers.isBrowser() && performance.mark) {
      performance.mark(name);
    }
  },

  /**
   * Measure performance between two markers
   * @param {string} startMark - Start marker name
   * @param {string} endMark - End marker name
   * @param {string} measureName - Measure name
   */
  measure: function(startMark, endMark, measureName) {
    if (browserHelpers.isBrowser() && performance.measure) {
      try {
        performance.measure(measureName, startMark, endMark);
      } catch (error) {
        console.warn('Performance measurement failed:', error);
      }
    }
  }
};

/**
 * Export all helpers
 */
export default {
  function: functionHelpers,
  object: objectHelpers,
  array: arrayHelpers,
  string: stringHelpers,
  math: mathHelpers,
  date: dateHelpers,
  browser: browserHelpers,
  performance: performanceHelpers
};
